import type { TargetSearchState } from "./state";

export const TARGET_SEARCH_TARGET_POINTS = 100;
export const TARGET_SEARCH_DISTRACTOR_PENALTY = 20;
export const TARGET_SEARCH_HINT_PENALTY = 10;

export interface TargetSearchScoringInput {
  readonly totalTargetCount: number;
  readonly foundTargetCount: number;
  readonly hintCount: number;
  readonly retryCount: number;
}

export interface TargetSearchScore extends TargetSearchScoringInput {
  readonly completionRate: number;
  readonly correctRate: number;
  readonly score: number;
}

export interface TargetSearchAttemptMetrics {
  readonly correctRate: number;
  readonly hintCount: number;
  readonly retryCount: number;
}

function normalizeCount(value: number): number {
  return Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;
}

function clampUnit(value: number): number {
  return Math.min(1, Math.max(0, value));
}

export function calculateTargetSearchScore(
  input: TargetSearchScoringInput
): TargetSearchScore {
  const totalTargetCount = normalizeCount(input.totalTargetCount);
  const foundTargetCount = Math.min(
    totalTargetCount,
    normalizeCount(input.foundTargetCount)
  );
  const hintCount = normalizeCount(input.hintCount);
  const retryCount = normalizeCount(input.retryCount);
  const selectionCount = foundTargetCount + retryCount;
  const completionRate =
    totalTargetCount === 0 ? 0 : foundTargetCount / totalTargetCount;
  const correctRate =
    selectionCount === 0 ? 0 : foundTargetCount / selectionCount;
  const availablePoints =
    totalTargetCount * TARGET_SEARCH_TARGET_POINTS;
  const earnedPoints =
    foundTargetCount * TARGET_SEARCH_TARGET_POINTS -
    retryCount * TARGET_SEARCH_DISTRACTOR_PENALTY -
    hintCount * TARGET_SEARCH_HINT_PENALTY;
  const score =
    availablePoints === 0
      ? 0
      : Math.round(clampUnit(earnedPoints / availablePoints) * 100);

  return {
    totalTargetCount,
    foundTargetCount,
    hintCount,
    retryCount,
    completionRate,
    correctRate,
    score
  };
}

export function scoreTargetSearchState(
  state: TargetSearchState
): TargetSearchScore {
  return calculateTargetSearchScore({
    totalTargetCount: state.rounds.reduce(
      (total, round) => total + round.targetIds.length,
      0
    ),
    foundTargetCount: state.selections.filter(
      ({ result }) => result === "target"
    ).length,
    hintCount: state.hintCount,
    retryCount: state.retryCount
  });
}

export function getTargetSearchAttemptMetrics(
  state: TargetSearchState
): TargetSearchAttemptMetrics {
  const score = scoreTargetSearchState(state);

  return {
    correctRate: score.correctRate,
    hintCount: score.hintCount,
    retryCount: score.retryCount
  };
}
